import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Button from '@decisiv/ui-components/lib/components/Button';
import Wizard from '@decisiv/ui-components/lib/components/Wizard';

import ProcessingPanel from './ProcessingPanel';
import SummaryPanel from './SummaryPanel';
import UploadStep from './UploadStep';
import validateFile from './fileValidation';
import { PROCESSING_PANEL, STEPS, SUMMARY_PANEL, WIZARD_TITLE } from './copy';
import { FooterActions } from './styles';
import { WIZARD_Z_INDEX } from '../CreateUserWizard/layers';
import useBulkUserImport, { PHASES } from '../../hooks/useBulkUserImport';

/**
 * The Create Multiple Users wizard (PD-4066).
 *
 * Two steps rather than the single-user wizard's four — Upload, then Summary —
 * because everything else about the users comes out of the CSV.
 *
 * The shape that matters is what happens between them. The upload is answered
 * with 202 and an id, the run happens in a worker, and the admin is told they
 * may close the window. So this component is not the owner of the outcome: it
 * polls while it is open, and the page behind it collects the same outcome from
 * the UserEvent feed whether this is open or not. Closing mid-run is a supported
 * ending, not an abort.
 */
function BulkCreateUserWizard({ onClose, onCompleted, visible }) {
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);

  const {
    importRecord,
    isUploading,
    phase,
    reset,
    upload,
    uploadError,
  } = useBulkUserImport();

  const isSummary = phase === PHASES.summary;
  const activeStep = phase === PHASES.upload ? 1 : 2;

  const chooseFile = (chosen) => {
    setFile(chosen);
    setFileError(validateFile(chosen));
  };

  const removeFile = () => {
    setFile(null);
    setFileError(null);
  };

  const close = () => {
    // A finished import has already been folded into the list by the toast path;
    // this covers the admin who stayed and watched it finish here.
    if (isSummary) onCompleted();

    setFile(null);
    setFileError(null);
    reset();
    onClose();
  };

  // Closing while it runs is explicitly allowed, and the page keeps watching, so
  // there is nothing to confirm and nothing to cancel.
  const closeWhileProcessing = () => {
    setFile(null);
    setFileError(null);
    reset();
    onClose();
  };

  const steps = [
    {
      title: STEPS.upload,
      hideBodyTitle: true,
      // CONTINUE is what sends the file. It is `nextButtonProps` rather than a
      // submit because the wizard has a second step after it; the KeyDS default
      // label for it is already "Continue", as the mockup has it.
      nextButtonProps: {
        disabled: !file || Boolean(fileError) || isUploading,
        // Where the mockup's "PROCESSING..." goes. It draws that state on the
        // upload button, but that button is gone by then — the file row has
        // replaced it — so the progress belongs on the control that is actually
        // working.
        loading: isUploading,
        onClick: () => upload(file),
      },
      content: (
        <UploadStep
          error={fileError || uploadError}
          file={file}
          isUploading={isUploading}
          onChooseFile={chooseFile}
          onRemoveFile={removeFile}
        />
      ),
    },
    {
      title: STEPS.summary,
      hideBodyTitle: true,
      content:
        isSummary && importRecord ? (
          <SummaryPanel importRecord={importRecord} />
        ) : (
          <ProcessingPanel />
        ),
    },
  ];

  return (
    <Wizard
      activeStep={activeStep}
      id="bulk-create-user-wizard"
      steps={steps}
      title={WIZARD_TITLE}
      visible={visible}
      zIndex={WIZARD_Z_INDEX}
      onCancel={close}
      renderActions={({ defaultActions, isLastStep }) => {
        if (!isLastStep) return defaultActions;

        // Processing: a disabled progress button, and a CLOSE that leaves the
        // run going — see the panel's own copy.
        if (!isSummary) {
          return (
            <FooterActions>
              {/* `loading` rather than `disabled`: the mockup draws a spinner in
                  this button, and loading disables it anyway. */}
              <Button
                loading
                loadingText={PROCESSING_PANEL.action}
                text={PROCESSING_PANEL.action}
              />
              <Button
                kind="secondary"
                text={PROCESSING_PANEL.close}
                onClick={closeWhileProcessing}
              />
            </FooterActions>
          );
        }

        return (
          <FooterActions>
            <Button text={SUMMARY_PANEL.close} onClick={close} />
          </FooterActions>
        );
      }}
    />
  );
}

BulkCreateUserWizard.propTypes = {
  onClose: PropTypes.func.isRequired,
  /** Called when an import finished in view, so the list can refetch. */
  onCompleted: PropTypes.func.isRequired,
  visible: PropTypes.bool,
};

BulkCreateUserWizard.defaultProps = {
  visible: false,
};

export default BulkCreateUserWizard;
